<template>
  <CloseHeader :title="`${t('delete_account_short')}`" />

  <div class="app-container one-page">
    <div class="secession-con">
      <div class="emoji-box">😢</div>
      <component :is="currentStep === 1 ? WithdrawStep01 : WithdrawStep02" ref="stepRef" />
    </div>

    <div class="app-btn-wrap">
      <button class="gray-btn l-btn" @click="handleCloseClick">
        <span>{{ t(leftButtonLabel) }}</span>
      </button>
      <button class="primary-btn l-btn" @click="handleNextClick">
        <span>{{ t(rightButtonLabel) }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { deleteUserSelfService, submitSurveyAnswer } from '@/api/apiService';
import CloseHeader from '@/components/domains/mobile/common/CloseHeader.vue';
import WithdrawStep01 from '@/components/domains/mobile/user-manage/WithdrawStep01.vue';
import WithdrawStep02 from '@/components/domains/mobile/user-manage/WithdrawStep02.vue';
import { useLogin } from '@/composables/shared/useLogin';
import { setIsBack } from '@/router';
import { usePopupStore } from '@/stores/popup';
import { useUserStore } from '@/stores/user';
import { logoutSocialMobile } from '@/utils/authService';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const userStore = useUserStore();
const popupStore = usePopupStore();

const currentStep = ref<number>(1);
const stepRef = useTemplateRef<InstanceType<typeof WithdrawStep01>>('stepRef');

const leftButtonLabel = computed(() => (currentStep.value === 1 ? 'label_continue' : 'button_cancel'));
const rightButtonLabel = computed(() => (currentStep.value === 1 ? 'next' : 'delete_account'));

const { goToLoginPage } = useLogin();

const handleCloseClick = () => {
  setIsBack(true);
  router.back();
};

const handleNextClick = async () => {
  if (currentStep.value === 1) {
    submitWithdrawReason();
  } else {
    withdrawUser();
  }
};

const validateWithdrawReason = (selectedOption, withdrawReason, isCustomReason): boolean => {
  if (!selectedOption) {
    popupStore.openToast(t('alert_select_option'));
    return false;
  }

  if (isCustomReason && !withdrawReason) {
    popupStore.openToast(t('alert_enter_detailed_reason'));

    return false;
  }

  return true;
};

const submitWithdrawReason = async () => {
  const { selectedOption, withdrawReason, isCustomReason } = stepRef.value!;

  if (!validateWithdrawReason(selectedOption, withdrawReason, isCustomReason)) {
    return;
  }

  try {
    const { success } = await submitSurveyAnswer({
      surveyType: 'DELETE_ACCOUNT',
      answer: isCustomReason ? withdrawReason : selectedOption,
    });

    if (success) {
      currentStep.value = 2;
    }
  } catch (error) {
    console.error('탈퇴 이유 저장 실패: ', error);
  }
};

const withdrawUser = async () => {
  try {
    const userId = userStore.userInfo.userId;
    const { success } = await deleteUserSelfService(userId);

    if (!success) {
      popupStore.openToast(t('alert_try_again'));
    }

    localStorage.removeItem('authToken');
    sessionStorage.removeItem('authToken');
    sessionStorage.removeItem('loginType');
    await logoutSocialMobile();
    goToLoginPage();

    //앱 로그인 화면으로 이동
  } catch (error) {
    console.error('탈퇴 실패: ', error);
  }
};

onBeforeMount(() => {
  if (route.query.tab) {
    currentStep.value = 2;
  }
});
</script>

<style scoped lang="scss">
.secession-con {
  .emoji-box {
    display: inline-block;
    padding: $size-s;
    border-radius: 50%;
    background: $gray10;
    font-size: $font-size-xl;
    margin: 1rem 0;
  }
}
.app-btn-wrap {
  padding-bottom: 1.25rem;
}
</style>
